import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Save } from 'lucide-react';
import { ConfirmDialog } from '../../docs/components/ConfirmDialog';
import { capabilityKeys, useUpdateCapability } from '../../../../services/capability.service';
import type { Capability } from '../../../../types/admin';
import {
  isJsonObject,
  joinList,
  parseJsonObject,
  parsePositiveIntList,
  splitList,
  type JsonObject,
} from '../utils';

const BYTES_PER_MB = 1024 * 1024;

type FormState = {
  serviceMenuEnabled: boolean;
  jobTimeoutSeconds: string;
  pollIntervalSeconds: string;
  maxConcurrentJobsPerUser: string;
  durationSecondsOptions: string;
  defaultDurationSeconds: string;
  aspectRatioOptions: string;
  defaultAspectRatio: string;
  maxPromptChars: string;
  maxInputImages: string;
  maxImageMb: string;
  allowedImageMimeTypes: string;
  outputDays: string;
  inputDays: string;
};

const section = (config: JsonObject, key: string): JsonObject =>
  isJsonObject(config[key]) ? (config[key] as JsonObject) : {};
const text = (value: unknown) => (value === undefined || value === null ? '' : String(value));

function toForm(config: JsonObject): FormState {
  const ui = section(config, 'ui');
  const execution = section(config, 'execution');
  const limits = section(config, 'limits');
  const retention = section(config, 'retention');
  return {
    serviceMenuEnabled: ui.serviceMenuEnabled !== false,
    jobTimeoutSeconds: text(execution.jobTimeoutSeconds),
    pollIntervalSeconds: text(execution.pollIntervalSeconds),
    maxConcurrentJobsPerUser: text(execution.maxConcurrentJobsPerUser),
    durationSecondsOptions: joinList(limits.durationSecondsOptions),
    defaultDurationSeconds: text(limits.defaultDurationSeconds),
    aspectRatioOptions: joinList(limits.aspectRatioOptions),
    defaultAspectRatio: text(limits.defaultAspectRatio),
    maxPromptChars: text(limits.maxPromptChars),
    maxInputImages: text(limits.maxInputImages),
    maxImageMb:
      typeof limits.maxImageBytes === 'number' ? String(limits.maxImageBytes / BYTES_PER_MB) : '',
    allowedImageMimeTypes: joinList(limits.allowedImageMimeTypes),
    outputDays: text(retention.outputDays),
    inputDays: text(retention.inputDays),
  };
}

const positiveInt = (value: string) => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};

/** Validate and fold the form into the capability config (unknown keys are preserved). */
function applyForm(
  config: JsonObject,
  form: FormState,
  hasImageLimits: boolean
): { config?: JsonObject; errors: Partial<Record<keyof FormState, string>> } {
  const errors: Partial<Record<keyof FormState, string>> = {};
  const intField = (key: keyof FormState) => {
    const value = positiveInt(String(form[key]));
    if (value === null) errors[key] = 'Positive whole number required';
    return value;
  };

  const durations = parsePositiveIntList(form.durationSecondsOptions);
  if (!durations) errors.durationSecondsOptions = 'Comma-separated positive seconds, e.g. 5, 8, 10';
  const defaultDuration = intField('defaultDurationSeconds');
  if (durations && defaultDuration !== null && !durations.includes(defaultDuration)) {
    errors.defaultDurationSeconds = 'Must be one of the duration options';
  }
  const ratios = splitList(form.aspectRatioOptions);
  if (ratios.length === 0) errors.aspectRatioOptions = 'At least one aspect ratio';
  if (!ratios.includes(form.defaultAspectRatio.trim())) {
    errors.defaultAspectRatio = 'Must be one of the aspect ratios';
  }

  const execution = {
    ...section(config, 'execution'),
    jobTimeoutSeconds: intField('jobTimeoutSeconds'),
    pollIntervalSeconds: intField('pollIntervalSeconds'),
    maxConcurrentJobsPerUser: intField('maxConcurrentJobsPerUser'),
  };
  const limits: JsonObject = {
    ...section(config, 'limits'),
    durationSecondsOptions: durations,
    defaultDurationSeconds: defaultDuration,
    aspectRatioOptions: ratios,
    defaultAspectRatio: form.defaultAspectRatio.trim(),
    maxPromptChars: intField('maxPromptChars'),
  };
  if (hasImageLimits) {
    limits.maxInputImages = intField('maxInputImages');
    const mb = Number(form.maxImageMb);
    if (!(mb > 0)) errors.maxImageMb = 'Size in MB greater than 0';
    limits.maxImageBytes = Math.round(mb * BYTES_PER_MB);
    const mimeTypes = splitList(form.allowedImageMimeTypes);
    if (mimeTypes.length === 0) errors.allowedImageMimeTypes = 'At least one image type';
    limits.allowedImageMimeTypes = mimeTypes;
  }
  const retention = {
    ...section(config, 'retention'),
    outputDays: intField('outputDays'),
    inputDays: intField('inputDays'),
  };

  if (Object.keys(errors).length > 0) return { errors };
  return {
    errors,
    config: {
      ...config,
      ui: { ...section(config, 'ui'), serviceMenuEnabled: form.serviceMenuEnabled },
      execution,
      limits,
      retention,
    },
  };
}

/**
 * Structured editor for the Generate Video part of `Capability.configSchema`. Values live in
 * D1; anything missing falls back to the code defaults, so "Restore defaults" clears D1.
 */
export const VideoServiceSettingsSection: React.FC<{ capability: Capability }> = ({
  capability,
}) => {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateCapability();
  // Edit what runtime actually uses (D1 merged over code defaults); save writes it back to D1.
  const effectiveSchema = capability.effectiveConfigSchema ?? capability.configSchema;
  const config = useMemo(() => parseJsonObject(effectiveSchema), [effectiveSchema]);
  const hasImageLimits = 'maxInputImages' in section(config, 'limits');
  const [form, setForm] = useState<FormState>(() => toForm(config));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [confirm, setConfirm] = useState<'save' | 'restore' | null>(null);
  const [pendingConfig, setPendingConfig] = useState<JsonObject | null>(null);

  useEffect(() => {
    setForm(toForm(config));
    setErrors({});
  }, [config]);

  const set = (key: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const persist = async (configSchema: string) => {
    await updateMutation.mutateAsync({ publicId: capability.publicId, configSchema });
    await queryClient.invalidateQueries({ queryKey: capabilityKeys.detail(capability.publicId) });
  };

  const requestSave = () => {
    const result = applyForm(config, form, hasImageLimits);
    setErrors(result.errors);
    if (!result.config) return;
    setPendingConfig(result.config);
    setConfirm('save');
  };

  const field = (key: keyof FormState, label: string, helper?: string) => (
    <TextField
      size="small"
      label={label}
      value={form[key]}
      onChange={set(key)}
      error={Boolean(errors[key])}
      helperText={errors[key] ?? helper}
      fullWidth
    />
  );

  return (
    <Paper className="rounded-xl p-6 shadow-sm">
      <Box className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Box>
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Generate Video settings
          </Typography>
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            Stored in D1. Missing values fall back to the code defaults.
          </Typography>
        </Box>
        <Box className="flex gap-2">
          <Button
            variant="outlined"
            startIcon={<RotateCcw size={16} />}
            onClick={() => setConfirm('restore')}
            disabled={updateMutation.isPending}
            sx={{ textTransform: 'none' }}
          >
            Restore defaults
          </Button>
          <Button
            variant="contained"
            startIcon={<Save size={16} />}
            onClick={requestSave}
            disabled={updateMutation.isPending}
            sx={{ textTransform: 'none' }}
          >
            Save settings
          </Button>
        </Box>
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={form.serviceMenuEnabled}
            onChange={(_event, checked) =>
              setForm((current) => ({ ...current, serviceMenuEnabled: checked }))
            }
          />
        }
        label="Show in the user Services menu"
      />

      <Typography variant="subtitle2" className="mb-2 mt-4 text-gray-700 dark:text-slate-300">
        Limits
      </Typography>
      <Box className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {field('durationSecondsOptions', 'Duration options (s)', 'e.g. 5, 8, 10')}
        {field('defaultDurationSeconds', 'Default duration (s)')}
        {field('aspectRatioOptions', 'Aspect ratios', 'e.g. 16:9, 9:16, 1:1')}
        {field('defaultAspectRatio', 'Default aspect ratio')}
        {field('maxPromptChars', 'Max prompt characters')}
        {hasImageLimits && field('maxInputImages', 'Max input images')}
        {hasImageLimits && field('maxImageMb', 'Max image size (MB)')}
        {hasImageLimits &&
          field('allowedImageMimeTypes', 'Image types', 'e.g. image/png, image/jpeg')}
      </Box>

      <Typography variant="subtitle2" className="mb-2 mt-4 text-gray-700 dark:text-slate-300">
        Execution & retention
      </Typography>
      <Box className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {field(
          'jobTimeoutSeconds',
          'Job timeout (s)',
          'Model call limit; the quota hold lasts this long'
        )}
        {field('pollIntervalSeconds', 'Poll interval (s)', 'Retry-After sent to clients')}
        {field('maxConcurrentJobsPerUser', 'Max running jobs per user')}
        {field('outputDays', 'Keep generated videos (days)')}
        {field('inputDays', 'Keep input images (days)')}
      </Box>

      <Alert severity="info" className="mt-4">
        Per-model limits (durations, aspect ratios, resolution label, image input mode) are set on
        each model mapping. Prices come from LLM Catalog → Unit Pricing (video_second / video).
      </Alert>

      <ConfirmDialog
        open={confirm === 'save'}
        title="Update Generate Video settings"
        message={`Save the new settings for ${capability.code}? They apply to new jobs immediately.`}
        confirmLabel="Save"
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          if (pendingConfig) void persist(JSON.stringify(pendingConfig));
        }}
      />
      <ConfirmDialog
        open={confirm === 'restore'}
        title="Restore defaults"
        message={`Replace the D1 config of ${capability.code} with the code defaults? They apply to new jobs immediately.`}
        confirmLabel="Restore"
        color="warning"
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          void persist(capability.defaultConfigSchema ?? '{}');
        }}
      />
    </Paper>
  );
};
